import type { Rect, UiRects } from "../../game/input";
import type { SoloMode } from "../../game/types";
import { easeOutCubic } from "../../shared/easing";
import type { RankedTier } from "../../versus/ranked";
import type { RenderModel } from "../renderModel";
import { GAME_FONT, resolveSoloLeaderboardSurfaceState, type PhoneMetrics } from "../renderConstants";
import { soloModeLabel, soloModeAccent } from "../../game/soloModes";
import type { LeaderboardScrollConfig } from "../leaderboardScrollController";

export interface PhoneHighscoresDeps {
  trimTextToWidth(text: string, maxWidth: number): string;
  drawLeaderboardTab(rect: Rect, label: string, active: boolean, color: string, fontSize: number): void;
  drawSoloLeaderboardModeChips(uiRects: UiRects, x: number, y: number, w: number, mobile: boolean, selected: SoloMode): number;
  drawHourglass(cx: number, cy: number, size: number, color: string): void;
  drawNotificationEmptyState(rect: Rect, title: string, subtitle: string, color: string, metrics: PhoneMetrics): void;
  drawNotificationPanel(rect: Rect, opts: { fill: string; stroke?: string; accent?: string; radius?: number; lineWidth?: number }): void;
  leaderboardStaggerAlpha(index: number, delayMs: number, durationMs: number): number;
  leaderboardTierColor(tier: RankedTier): string;
  nowSec(): number;
  leaderboardScreenEnterTime: number;
  resolveLeaderboardScroll(config: LeaderboardScrollConfig): number;
}

export function drawPhoneHighscoresScreen(
  deps: PhoneHighscoresDeps,
  ctx: CanvasRenderingContext2D, model: RenderModel, uiRects: UiRects,
  scrX: number, scrY: number, scrW: number, scrH: number,
  contentX: number, contentW: number, contentRight: number,
  metrics: PhoneMetrics,
): void {
  const desktop = metrics.desktop;
  const trimToWidth = (text: string, maxWidth: number): string => deps.trimTextToWidth(text, maxWidth);

  let curY = scrY + metrics.topPad;
  ctx.fillStyle = "#ffd700";
  ctx.font = `bold ${metrics.titleFontPx}px ${GAME_FONT}`;
  ctx.textAlign = "center";
  ctx.textBaseline = "top";
  ctx.fillText("HIGH SCORES", scrX + scrW / 2, curY);
  curY += metrics.titleGap;

  // ── Two top buttons (like friends page) ──
  const btnGap = 4;
  const btnH = metrics.ctaH;
  const halfW = Math.floor((contentW - btnGap) / 2);
  const isSolo = model.leaderboardTab === "solo";

  const soloRect: Rect = { x: contentX, y: curY, w: halfW, h: btnH };
  const versusRect: Rect = { x: contentX + halfW + btnGap, y: curY, w: halfW, h: btnH };
  uiRects.leaderboardTabSolo = soloRect;
  uiRects.leaderboardTabVersus = versusRect;

  // Solo button
  ctx.fillStyle = isSolo ? "#1a2a10" : "#0a1520";
  ctx.fillRect(soloRect.x, soloRect.y, soloRect.w, soloRect.h);
  ctx.strokeStyle = isSolo ? "#ffd700" : "#5a7a9a";
  ctx.lineWidth = 1;
  ctx.strokeRect(soloRect.x, soloRect.y, soloRect.w, soloRect.h);
  ctx.fillStyle = isSolo ? "#ffd700" : "#5a7a9a";
  ctx.font = `bold ${metrics.actionFontPx}px ${GAME_FONT}`;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText("SCORES", soloRect.x + soloRect.w / 2, soloRect.y + soloRect.h / 2);

  // Versus button
  ctx.fillStyle = !isSolo ? "#1a1020" : "#0a1520";
  ctx.fillRect(versusRect.x, versusRect.y, versusRect.w, versusRect.h);
  ctx.strokeStyle = !isSolo ? "#ff8a7c" : "#5a7a9a";
  ctx.lineWidth = 1;
  ctx.strokeRect(versusRect.x, versusRect.y, versusRect.w, versusRect.h);
  ctx.fillStyle = !isSolo ? "#ff8a7c" : "#5a7a9a";
  ctx.font = `bold ${metrics.actionFontPx}px ${GAME_FONT}`;
  ctx.fillText("RANKINGS", versusRect.x + versusRect.w / 2, versusRect.y + versusRect.h / 2);

  curY += btnH + metrics.sectionGap;

  if (model.leaderboardTab === "solo") {
    // ── Mode cycle selector (tap to toggle through modes) ──
    const modeLabel = soloModeLabel(model.selectedSoloLeaderboardMode).toUpperCase();
    const modeAccent = soloModeAccent(model.selectedSoloLeaderboardMode);
    const cycleBtnH = desktop ? 18 : 16;
    const cycleRect: Rect = { x: contentX, y: curY, w: contentW, h: cycleBtnH };
    uiRects.soloLeaderboardCycleNext = cycleRect;

    ctx.fillStyle = "rgba(10,18,34,0.7)";
    ctx.fillRect(cycleRect.x, cycleRect.y, cycleRect.w, cycleRect.h);
    ctx.strokeStyle = modeAccent;
    ctx.lineWidth = 1;
    ctx.strokeRect(cycleRect.x, cycleRect.y, cycleRect.w, cycleRect.h);
    ctx.fillStyle = modeAccent;
    ctx.font = `bold ${metrics.sectionFontPx}px ${GAME_FONT}`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(`◄  ${modeLabel}  ►`, cycleRect.x + cycleRect.w / 2, cycleRect.y + cycleRect.h / 2);

    curY += cycleBtnH + metrics.sectionGap;
    const soloLeaderboard = resolveSoloLeaderboardSurfaceState(
      model.leaderboard.slice(0, 100),
      model.leaderboardLoading,
      model.leaderboardMode,
      model.leaderboardDisplayMode
    );
    const entries = soloLeaderboard.entries;
    const headerY = curY;
    const rowH = metrics.leaderboardRowH;
    const listTop = headerY + 10;
    const listBottom = scrY + scrH - 6;
    const listH = Math.max(0, listBottom - listTop);
    uiRects.leaderboardScrollViewport = { x: contentX, y: listTop, w: contentW, h: listH };

    ctx.fillStyle = "#5a7a9a";
    ctx.font = `bold ${metrics.helperFontPx}px ${GAME_FONT}`;
    ctx.textAlign = "left";
    ctx.textBaseline = "top";
    ctx.fillText("#", contentX + 2, headerY);
    ctx.fillText("NAME", contentX + 20, headerY);
    ctx.textAlign = "right";
    ctx.fillText("SCORE", contentRight - 2, headerY);
    ctx.strokeStyle = "rgba(130,170,255,0.25)";
    ctx.beginPath();
    ctx.moveTo(contentX, headerY + 8);
    ctx.lineTo(contentRight, headerY + 8);
    ctx.stroke();

    if (entries.length === 0 && soloLeaderboard.loading) {
      deps.drawHourglass(scrX + scrW / 2, listTop + Math.min(26, listH / 2), 18, "#ffd700");
      deps.drawNotificationEmptyState(
        { x: contentX, y: listTop, w: contentW, h: listH },
        "LOADING SCORES",
        "FETCHING THE LATEST BOARD",
        "#ffd700",
        metrics
      );
      return;
    }
    if (entries.length === 0) {
      deps.drawNotificationEmptyState(
        { x: contentX, y: listTop, w: contentW, h: listH },
        "NO SCORES YET",
        "BE THE FIRST TO POST ONE",
        "#ffd700",
        metrics
      );
      return;
    }

    const totalHeight = entries.length * rowH;
    const maxOffset = Math.max(0, totalHeight - listH);
    const scrollSpeed = 24;
    const scrollOffset = deps.resolveLeaderboardScroll({
      kind: "continuous",
      maxOffset,
      speedPxPerSec: scrollSpeed
    });

    ctx.save();
    ctx.beginPath();
    ctx.rect(contentX, listTop, contentW, listH);
    ctx.clip();

    for (let i = 0; i < entries.length; i += 1) {
      const item = entries[i];
      const rowY = listTop + i * rowH - scrollOffset;
      if (rowY + rowH < listTop || rowY > listTop + listH) {
        continue;
      }

      const rowAlpha = deps.leaderboardStaggerAlpha(i, 40, 160);
      if (rowAlpha <= 0) {
        continue;
      }
      if (rowAlpha < 1) {
        ctx.save();
        ctx.globalAlpha = easeOutCubic(rowAlpha);
        ctx.translate(12 * (1 - easeOutCubic(rowAlpha)), 0);
      }

      if (desktop) {
        deps.drawNotificationPanel(
          { x: contentX, y: rowY, w: contentW, h: rowH - 1 },
          {
            fill: i % 2 === 0 ? "rgba(24,35,58,0.92)" : "rgba(18,28,48,0.92)",
            stroke: "rgba(92,140,212,0.14)",
            radius: 4
          }
        );
      } else {
        ctx.fillStyle = i % 2 === 0 ? "rgba(110,76,188,0.18)" : "rgba(68,98,168,0.14)";
        ctx.fillRect(contentX, rowY, contentW, rowH - 2);
      }

      ctx.font = `bold ${metrics.valueFontPx}px ${GAME_FONT}`;
      ctx.textAlign = "left";
      ctx.textBaseline = "middle";
      if (i < 3) {
        ctx.fillStyle = i === 0 ? "#ffd700" : i === 1 ? "#c0c0c0" : "#cd7f32";
      } else {
        ctx.fillStyle = "#c8d8f0";
      }
      ctx.fillText(`${i + 1}.`, contentX + 2, rowY + rowH / 2 - 1);

      ctx.fillStyle = "#c8d8f0";
      ctx.font = `bold ${metrics.sectionFontPx}px ${GAME_FONT}`;
      ctx.fillText(trimToWidth(item.name, contentW - 86), contentX + 20, rowY + rowH / 2 - 1);

      ctx.textAlign = "right";
      ctx.fillStyle = i === 0 ? "#ffd700" : "#c8d8f0";
      ctx.fillText(Math.floor(item.score).toLocaleString(), contentRight - 2, rowY + rowH / 2 - 1);

      if (rowAlpha < 1) {
        ctx.restore();
      }
    }

    ctx.restore();
    return;
  }

  const entries = model.versusLeaderboard.slice(0, 100);
  const rankContext = model.versusRankContext;
  const player = rankContext?.player ?? null;
  const currentUserId = model.authState.user?.id ?? null;
  const signedInRankedUser = currentUserId != null && currentUserId !== "guest-local";
  const summaryY = curY;
  const summaryH = metrics.summaryH;

  deps.drawNotificationPanel(
    { x: contentX, y: summaryY, w: contentW, h: summaryH },
    { fill: "rgba(10,18,34,0.9)", stroke: "rgba(122,164,230,0.35)", radius: metrics.cardRadius }
  );

  if (player != null) {
    const accent = deps.leaderboardTierColor(player.tier);
    const rankText = player.rank == null
      ? `UNRANKED / ${rankContext?.totalEligiblePlayers ?? 0}`
      : `#${player.rank} / ${rankContext?.totalEligiblePlayers ?? 0}`;
    const detailText = player.provisional
      ? `PROVISIONAL ${Math.min(10, player.rankedGames)}/10`
      : `W ${player.rankedWins}  L ${player.rankedLosses}  DC ${player.rankedDisconnects}`;

    ctx.textAlign = "left";
    ctx.textBaseline = "top";
    ctx.fillStyle = accent;
    ctx.font = `bold ${metrics.sectionFontPx}px ${GAME_FONT}`;
    ctx.fillText(player.tier.toUpperCase(), contentX + 8, summaryY + 7);
    ctx.fillStyle = "#f6fbff";
    ctx.font = `bold ${desktop ? metrics.valueFontPx + 1 : 7}px ${GAME_FONT}`;
    ctx.fillText(`RATING ${player.rating}`, contentX + 8, summaryY + (desktop ? 18 : 17));
    ctx.fillStyle = "#9cc2ff";
    ctx.font = `bold ${metrics.helperFontPx}px ${GAME_FONT}`;
    ctx.fillText(rankText, contentX + 8, summaryY + (desktop ? 31 : 28));
    ctx.textAlign = "right";
    ctx.fillStyle = player.provisional ? "#ffb37a" : "#7ce5d8";
    ctx.fillText(trimToWidth(detailText, contentW - 16), contentRight - 8, summaryY + (desktop ? 31 : 28));
  } else {
    const summaryMessage = signedInRankedUser
      ? "PLAY QUICKMATCH TO PLACE ON THE LADDER"
      : "SIGN IN TO PLACE ON THE LADDER";
    deps.drawNotificationEmptyState(
      { x: contentX, y: summaryY, w: contentW, h: summaryH },
      summaryMessage,
      "VALIDATED QUICKMATCH RESULTS ONLY",
      "#ff8a7c",
      metrics
    );
  }

  const headerY = summaryY + summaryH + 8;
  const rowH = metrics.leaderboardRowH;
  const listTop = headerY + 10;
  const listBottom = scrY + scrH - 6;
  const listH = Math.max(0, listBottom - listTop);
  uiRects.leaderboardScrollViewport = { x: contentX, y: listTop, w: contentW, h: listH };

  ctx.fillStyle = "#5a7a9a";
  ctx.font = `bold ${metrics.helperFontPx}px ${GAME_FONT}`;
  ctx.textAlign = "left";
  ctx.textBaseline = "top";
  ctx.fillText("#", contentX + 2, headerY);
  ctx.fillText("NAME", contentX + 20, headerY);
  ctx.textAlign = "right";
  ctx.fillText("RATING", contentRight - 2, headerY);
  ctx.strokeStyle = "rgba(130,170,255,0.25)";
  ctx.beginPath();
  ctx.moveTo(contentX, headerY + 8);
  ctx.lineTo(contentRight, headerY + 8);
  ctx.stroke();

  if (entries.length === 0 && model.versusLeaderboardLoading) {
    deps.drawHourglass(scrX + scrW / 2, listTop + Math.min(26, listH / 2), 18, "#ff8a7c");
    deps.drawNotificationEmptyState(
      { x: contentX, y: listTop, w: contentW, h: listH },
      "LOADING LADDER",
      "FETCHING RANKED PLAYERS",
      "#ff8a7c",
      metrics
    );
    return;
  }
  if (entries.length === 0) {
    deps.drawNotificationEmptyState(
      { x: contentX, y: listTop, w: contentW, h: listH },
      "NO RANKED PLAYERS YET",
      "PLAY QUICKMATCH TO PLACE",
      "#ff8a7c",
      metrics
    );
    return;
  }

  const totalHeight = entries.length * rowH;
  const maxOffset = Math.max(0, totalHeight - listH);
  const scrollSpeed = 22;
  const scrollOffset = deps.resolveLeaderboardScroll({
    kind: "continuous",
    maxOffset,
    speedPxPerSec: scrollSpeed
  });

  ctx.save();
  ctx.beginPath();
  ctx.rect(contentX, listTop, contentW, listH);
  ctx.clip();

  for (let i = 0; i < entries.length; i += 1) {
    const entry = entries[i];
    const rowY = listTop + i * rowH - scrollOffset;
    if (rowY + rowH < listTop || rowY > listTop + listH) {
      continue;
    }

    const rowAlpha = deps.leaderboardStaggerAlpha(i, 40, 160);
    if (rowAlpha <= 0) {
      continue;
    }
    if (rowAlpha < 1) {
      ctx.save();
      ctx.globalAlpha = easeOutCubic(rowAlpha);
      ctx.translate(12 * (1 - easeOutCubic(rowAlpha)), 0);
    }

    const highlighted = currentUserId != null && entry.userId === currentUserId;
    if (desktop) {
      deps.drawNotificationPanel(
        { x: contentX, y: rowY, w: contentW, h: rowH - 1 },
        {
          fill: highlighted
            ? "rgba(0,255,206,0.12)"
            : i % 2 === 0 ? "rgba(24,35,58,0.92)" : "rgba(18,28,48,0.92)",
          stroke: highlighted ? "rgba(0,255,220,0.65)" : "rgba(92,140,212,0.14)",
          radius: 4
        }
      );
    } else {
      ctx.fillStyle = highlighted
        ? "rgba(0,255,206,0.12)"
        : i % 2 === 0 ? "rgba(110,76,188,0.18)" : "rgba(68,98,168,0.14)";
      ctx.fillRect(contentX, rowY, contentW, rowH - 2);
      if (highlighted) {
        ctx.strokeStyle = "rgba(0,255,220,0.65)";
        ctx.strokeRect(contentX, rowY, contentW, rowH - 2);
      }
    }

    ctx.font = `bold ${metrics.valueFontPx}px ${GAME_FONT}`;
    ctx.textAlign = "left";
    ctx.textBaseline = "middle";
    ctx.fillStyle = entry.rank === 1 ? "#ffd700" : entry.rank === 2 ? "#c0c0c0" : entry.rank === 3 ? "#cd7f32" : "#c8d8f0";
    ctx.fillText(`${entry.rank}.`, contentX + 2, rowY + rowH / 2 - 1);

    ctx.fillStyle = highlighted ? "#8bfff5" : deps.leaderboardTierColor(entry.tier);
    ctx.font = `bold ${metrics.sectionFontPx}px ${GAME_FONT}`;
    ctx.fillText(trimToWidth(entry.displayName, contentW - 80), contentX + 20, rowY + rowH / 2 - 1);

    ctx.textAlign = "right";
    ctx.fillStyle = highlighted ? "#7effff" : "#e6f4ff";
    ctx.fillText(String(entry.rating), contentRight - 2, rowY + rowH / 2 - 1);

    if (rowAlpha < 1) {
      ctx.restore();
    }
  }

  ctx.restore();
}
